const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;
const MAX_INPUT_BYTES = 50 * 1024 * 1024;
const MAX_INPUT_PIXELS = 25_000_000;
const NORMALIZED_LONG_EDGE = 2048;
const OUTPUT_LONG_EDGE = 1024;
const MIN_OUTPUT_EDGE = 256;
const MAX_OUTPUT_EDGE = 2048;
const MAX_OUTPUT_PIXELS = 2_097_152;

export interface NormalizedPhoto {
  blob: Blob;
  height: number;
  width: number;
}

export interface ImageDimensions {
  height: number;
  width: number;
}

function roundToMultipleOf16(value: number): number {
  return Math.max(16, Math.round(value / 16) * 16);
}

export function calculateEditDimensions(sourceWidth: number, sourceHeight: number): ImageDimensions {
  if (!Number.isFinite(sourceWidth) || !Number.isFinite(sourceHeight) || sourceWidth <= 0 || sourceHeight <= 0) {
    throw new Error('Invalid source image dimensions.');
  }

  let scale = OUTPUT_LONG_EDGE / Math.max(sourceWidth, sourceHeight);
  if (Math.min(sourceWidth, sourceHeight) * scale < MIN_OUTPUT_EDGE) {
    scale = MIN_OUTPUT_EDGE / Math.min(sourceWidth, sourceHeight);
  }
  if (Math.max(sourceWidth, sourceHeight) * scale > MAX_OUTPUT_EDGE) {
    scale = MAX_OUTPUT_EDGE / Math.max(sourceWidth, sourceHeight);
  }

  let width = Math.min(MAX_OUTPUT_EDGE, Math.max(MIN_OUTPUT_EDGE, roundToMultipleOf16(sourceWidth * scale)));
  let height = Math.min(MAX_OUTPUT_EDGE, Math.max(MIN_OUTPUT_EDGE, roundToMultipleOf16(sourceHeight * scale)));

  if (width * height > MAX_OUTPUT_PIXELS) {
    const pixelScale = Math.sqrt(MAX_OUTPUT_PIXELS / (width * height));
    width = Math.max(MIN_OUTPUT_EDGE, Math.floor((width * pixelScale) / 16) * 16);
    height = Math.max(MIN_OUTPUT_EDGE, Math.floor((height * pixelScale) / 16) * 16);
  }

  return { width, height };
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);

    const cleanup = () => URL.revokeObjectURL(objectUrl);
    image.addEventListener('load', () => {
      cleanup();
      resolve(image);
    }, { once: true });
    image.addEventListener('error', () => {
      cleanup();
      reject(new Error('Unable to decode the selected photo.'));
    }, { once: true });
    image.src = objectUrl;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, type: 'image/jpeg' | 'image/png'): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error('Unable to encode the selected photo.'));
      }
    }, type, type === 'image/jpeg' ? 0.9 : undefined);
  });
}

export async function normalizePhoto(file: File): Promise<NormalizedPhoto> {
  const looksLikeImage = file.type.startsWith('image/') || /\.(heic|heif|jpe?g|png|webp)$/i.test(file.name);
  if (!looksLikeImage || file.size > MAX_INPUT_BYTES) {
    throw new Error('Unsupported photo.');
  }

  const image = await loadImage(file);
  if (image.naturalWidth * image.naturalHeight > MAX_INPUT_PIXELS) {
    throw new Error('The selected photo is too large.');
  }

  const scale = Math.min(1, NORMALIZED_LONG_EDGE / Math.max(image.naturalWidth, image.naturalHeight));
  const width = Math.max(1, Math.round(image.naturalWidth * scale));
  const height = Math.max(1, Math.round(image.naturalHeight * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Canvas is unavailable.');
  }
  context.drawImage(image, 0, 0, width, height);

  const preferredType: 'image/jpeg' | 'image/png' = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
  let blob = await canvasToBlob(canvas, preferredType);
  if (blob.size > MAX_UPLOAD_BYTES && preferredType === 'image/png') {
    blob = await canvasToBlob(canvas, 'image/jpeg');
  }
  if (blob.size > MAX_UPLOAD_BYTES) {
    throw new Error('The selected photo is too large to upload.');
  }

  return { blob, width, height };
}
